import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Menu, MenuItem, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useAppId } from "../../hook";
import { ActionType } from "../../reducer";
import { useAiIdentity } from "../../slices/ui";
import { queryOmegaSingleResult } from "../../api";
import { toEDNString } from "edn-data";

const DS = `(datastore Qo.Api.ServiceAccount "omega/query-omega/api/service-account")`;
const ednStr = (s: string) => toEDNString(s);
type Acct = { "service-account-id": string; name: string };

export const AccountSwitcher: React.FC = () => {
  const dispatch = useDispatch();
  const [appId] = useAppId();
  const identity = useAiIdentity();
  const [accounts, setAccounts] = useState<Acct[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [q, setQ] = useState("");
  const open = Boolean(anchorEl);

  // App-scoped read — same convention as the rest of the API (app-id only, no auth-token).
  useEffect(() => {
    if (!appId) return;
    queryOmegaSingleResult(`${DS}(Qo.Api.ServiceAccount/list ${ednStr(appId)} R)(return [R])`)
      .then((r) => setAccounts(Array.isArray(r) ? r : []))
      .catch(() => setAccounts([]));
  }, [appId]);

  const label = identity.kind === "self" ? "My account" : identity.name;

  // U.4 — search filters accounts client-side (many service accounts can
  // share a name; the id caption below disambiguates them).
  const filteredAccounts = accounts.filter((a) =>
    (a.name + a["service-account-id"]).toLowerCase().includes(q.toLowerCase())
  );

  const pickSelf = () => { setAnchorEl(null); setQ(""); dispatch({ type: ActionType.SET_AI_IDENTITY, identity: { kind: "self" } }); };
  const pickAccount = async (a: Acct) => {
    setAnchorEl(null);
    setQ("");
    const q = `${DS}(Qo.Api.ServiceAccount/service-account-key ${ednStr(appId!)} ${ednStr(a["service-account-id"])} R)(return [R])`;
    // Only the workspace's owner, an Admin or root may read an account's key (query-omega-oql
    // perm/service-account.oql); a denied read leaves the identity unchanged, like a missing key.
    const key = (await queryOmegaSingleResult(q).catch(() => "")) as string;
    if (!key) return; // no key resolved — leave identity unchanged
    dispatch({ type: ActionType.SET_AI_IDENTITY,
      identity: { kind: "service-account", serviceAccountId: a["service-account-id"], apiKey: key, name: a.name } });
  };

  return (
    <>
      <Box data-testid="ai-account-switcher" role="button" aria-haspopup="listbox"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", maxWidth: 160 }}>
        <Typography noWrap sx={{ fontSize: "0.75rem", fontWeight: 600 }}>{label}</Typography>
        <ExpandMoreIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
      </Box>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}
        PaperProps={{ elevation: 3, sx: { minWidth: 240 } }}>
        <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            placeholder="Search accounts…"
            inputProps={{ "data-testid": "ai-account-search" }}
          />
        </Box>
        <MenuItem data-testid="ai-account-self" dense onClick={pickSelf} sx={{ gap: 1.5 }}>
          <Typography sx={{ fontSize: "0.875rem", flex: 1 }}>My account</Typography>
          {identity.kind === "self" && <CheckIcon sx={{ fontSize: "0.875rem", color: "primary.main" }} />}
        </MenuItem>
        {filteredAccounts.map((a) => {
          const active = identity.kind === "service-account" && identity.serviceAccountId === a["service-account-id"];
          return (
            <MenuItem key={a["service-account-id"]} data-testid={`ai-account-${a["service-account-id"]}`}
              dense onClick={() => pickAccount(a)} sx={{ gap: 1.5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <Typography variant="body2">{a.name}</Typography>
                <Typography variant="caption" color="text.secondary">{a["service-account-id"]}</Typography>
              </Box>
              {active && <CheckIcon sx={{ fontSize: "0.875rem", color: "primary.main" }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
